import React, { useState } from "react";
import {
  Box,
  Text,
  Button,
  Group,
  RingProgress,
  createStyles,
  Loader,
  Stack,
  Tooltip,
} from "@mantine/core";
import { IconFingerprint } from "@tabler/icons-react";
import { useGetTimeUtilization } from "api/attendance/gettodayTimeUtilization";
import {
  Location,
  PunchInPayload,
  useAddPunchIn,
} from "api/attendance/punchIn";
import { getDeviceId } from "utils/deviceid";
import { getCurrentLocation } from "utils/devicegeolocation";
import PunchOutModal from "../punchOutModal";

const useStyles = createStyles((theme) => ({
  card: {
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    boxShadow: theme.shadows.sm,
    paddingLeft: 24,
    paddingRight: 24,
    width: "33%",
    display: "flex", // Make card a flex container
    flexDirection: "column",
    justifyContent: "center", // Center vertically
  },
  contentWrapper: {
    width: "100%",
  },
  ringWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    margin: "auto", // Center the ring horizontally
  },
  statsGroup: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing.md,
    width: "100%",
    gap: theme.spacing.md,
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flex: 1,
    justifyContent: "center",
    padding: theme.spacing.xs,
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.sm,
  },
}));

interface TimeUtilCardProps {
  userId?: string | null;
}

export function TimeUtilCard({ userId }: TimeUtilCardProps) {
  const { classes, theme } = useStyles();
  const { data, isLoading } = useGetTimeUtilization(userId || undefined);
  const { mutateAsync: addPunchIn, data: punchInResponse } = useAddPunchIn();
  const [opened, setOpened] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);

  // Determine if viewing another user
  const isViewingOtherUser = userId !== null && userId !== undefined;

  const canPunchOut = data?.data?.canPunchOut ?? false;
  const punchInTime = data?.data?.punchInTime || "";
  let buttonContent;

  if (loadingAction) {
    buttonContent = <Loader size="xs" />;
  } else if (canPunchOut) {
    buttonContent = "Clock Out";
  } else {
    buttonContent = "Clock In";
  }
  const timeString = new Date(punchInTime).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const handlePunchIn = async () => {
    setLoadingAction(true);
    try {
      const timestamp = new Date().toISOString();

      const deviceId = getDeviceId();

      let location: Location = { lat: 0, lng: 0 };

      try {
        const { lat, lng } = await getCurrentLocation();
        location = { lat, lng };
      } catch (geoError) {
        console.log(geoError);
      }
      const payload = {
        punchInTime: timestamp,
        source: "Web",
        deviceId,
        location,
      };

      await addPunchIn(payload);
    } catch (err) {
      console.error("Clock In Failed:", err);
    } finally {
      setLoadingAction(false);
    }
  };

  return (
    <Box className={classes.card}>
      <Box className={classes.contentWrapper}>
        <Group>
          <Text weight={500} size="lg" mb={30}>
            Today Time Utilisation
          </Text>
        </Group>

        {isLoading ? (
          <Box mt={20} style={{ display: "flex", justifyContent: "center" }}>
            <Loader size="sm" />
          </Box>
        ) : (
          <>
            <Group position="apart" align="center" mt="md">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <IconFingerprint color="green" size={24} />
                <Text weight={500} size="sm">
                  Clock In: {canPunchOut ? timeString : ""}
                </Text>
              </div>

              <Tooltip
                label="Cannot clock in/out for other users"
                disabled={!isViewingOtherUser}
              >
                <Button
                  variant="filled"
                  radius="md"
                  size="sm"
                  onClick={canPunchOut ? () => setOpened(true) : handlePunchIn}
                  disabled={loadingAction || isViewingOtherUser}
                >
                  {buttonContent}
                </Button>
              </Tooltip>
            </Group>

            <Box className={classes.ringWrapper} mt={24}>
              <RingProgress
                size={200}
                thickness={8}
                sections={[
                  {
                    value: data?.data?.workingPercentage ?? 0,
                    color: "#4B5BFF",
                  },
                ]}
                label={
                  <div
                    style={{
                      textAlign: "center",
                      flexDirection: "column",
                      display: "flex",
                    }}
                  >
                    <Text size="lg" weight={500} mt={12}>
                      Working
                    </Text>
                    <Text size="xl" weight={700}>
                      {data?.data?.workingPercentage ?? 0}%
                    </Text>
                  </div>
                }
              />
            </Box>

            <Box className={classes.statsGroup} mt={24}>
              <Box className={classes.statItem}>
                <Text>Break: {data?.data?.breakTime ?? 0} </Text>
              </Box>
              <Box className={classes.statItem}>
                <Text>Overtime: {data?.data?.overtime ?? 0}</Text>
              </Box>
            </Box>
          </>
        )}
        <PunchOutModal opened={opened} onClose={() => setOpened(false)} />
      </Box>
    </Box>
  );
}
